import type { TFunction } from 'i18next';

/** GERP 저장부자재 발주(125) — 레거시 appr_edit_forms_sub_order_mstr */
export const STORAGE_MATERIAL_ORDER_TYPE = 125;

export const isStorageMaterialOrderType = (apvType: number): boolean =>
  apvType === STORAGE_MATERIAL_ORDER_TYPE;

/** 레거시 save_doc_sub_order_mstr 알럿 — 임시저장·상신 모두 */
export const STORAGE_MATERIAL_ORDER_MISSING_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';

/** GERP 미상신 결재 건 — 키는 GERP 응답 그대로 */
export type StorageMaterialOrderCandidate = {
  sysId: string;
  aprvId: string;
  aprvTypcd: string;
  title?: string | null;
  docTit?: string | null;
};

type Value = string | number | null | undefined;

/** 발주 라인 — 키는 GERP 응답 그대로 */
export type StorageMaterialOrderLine = {
  smatClassName?: Value;
  smatDivisionName?: Value;
  smatStdName?: Value;
  smatFormName?: Value;
  unitPrice?: Value;
  stockQuantity?: Value;
  invAmt?: Value;
  custName?: Value;
  qty?: Value;
  orderAmt?: Value;
};

export type StorageMaterialOrderCard = {
  aprvId?: Value;
  orderNo?: Value;
  orderDate?: Value;
  brand?: Value;
  vdNm?: Value;
  lines?: StorageMaterialOrderLine[] | null;
};

export type StorageMaterialOrderDraftSource = {
  code: number;
  attributes: Record<string, unknown>;
  display: {
    candidates: StorageMaterialOrderCandidate[] | null;
    selection: string[] | null;
    subject: string | null;
    content: { cards?: StorageMaterialOrderCard[] | null } | null;
  };
};

/** 저장 attributes 와 같은 모양 */
export type StorageMaterialOrderState = {
  systemId: string;
  approvalTypeCode: string;
  approvalId: string;
  /** 작성 시점 본문 HTML */
  content: string | null;
};

const text = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v : null;

export const parseStorageMaterialOrderAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): StorageMaterialOrderState | null => {
  const systemId = text(attributes?.systemId);
  const approvalTypeCode = text(attributes?.approvalTypeCode);
  const approvalId = text(attributes?.approvalId);
  if (!attributes || !systemId || !approvalTypeCode || !approvalId) return null;
  return {
    systemId,
    approvalTypeCode,
    approvalId,
    content: text(attributes.content),
  };
};

export const parseStorageMaterialOrderContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => text(attributes?.content);

export const buildStorageMaterialOrderAttributes = (
  state: StorageMaterialOrderState | null | undefined,
) =>
  state
    ? {
        systemId: state.systemId,
        approvalTypeCode: state.approvalTypeCode,
        approvalId: state.approvalId,
        content: state.content,
      }
    : null;

export const checkStorageMaterialOrderBeforeSave = (
  state: StorageMaterialOrderState | null | undefined,
): string | null =>
  state?.content ? null : STORAGE_MATERIAL_ORDER_MISSING_MESSAGE;

export const storageMaterialOrderKey = (
  state: StorageMaterialOrderState | null | undefined,
) => state?.approvalId ?? '';

export const buildStorageMaterialOrderCandidateLabel = (
  c: StorageMaterialOrderCandidate,
) => c.docTit?.trim() || c.title?.trim() || c.aprvId;

const plain = (v: Value) =>
  v === null || v === undefined ? '' : String(v).trim();

/** 전사 브랜드 코드 기준. GERP 응답엔 코드만 와서 프론트가 이름으로 바꿈 */
const BRAND_NAMES: Record<string, string> = {
  M: 'MLB',
  I: 'MLB Kids',
  X: 'Discovery',
  V: 'Duvetica',
  W: 'Supra',
  ST: 'Sergio Tacchini',
};

/** 대소문자 구분 없이 찾고, 표에 없는 코드는 그대로 */
export const toStorageMaterialOrderBrandName = (brand: Value) => {
  const code = plain(brand);
  return BRAND_NAMES[code.toUpperCase()] ?? code;
};

/** 레거시 titleChange — `{브랜드명} 저장 부자재 발주의 건({발주번호}-{업체명})` */
export const buildStorageMaterialOrderSubject = (
  card: StorageMaterialOrderCard,
  t: TFunction,
) =>
  t('{{brand}} 저장 부자재 발주의 건({{orderNo}}-{{vendor}})', {
    brand: toStorageMaterialOrderBrandName(card.brand),
    orderNo: plain(card.orderNo),
    vendor: plain(card.vdNm),
    interpolation: { escapeValue: false },
  });
